<template>
  <div class="h-full flex flex-col p-4">
    <base-dialog
      :show="!!error"
      title="An error occurred!"
      @close="handleError"
    >
      <p>{{ error }}</p>
    </base-dialog>
    <base-toast
      v-if="!!baseToast.message"
      :message="baseToast.message"
      :type="baseToast.type"
      @close="handleBaseToast"
    ></base-toast>
    <section class="h-full flex flex-col overflow-y-auto">
      <base-card class="h-full flex flex-col">
        <!-- Header with Search Button -->
        <div class="flex justify-between items-center px-4 py-3">
          <div class="flex justify-start items-center flex-wrap gap-4">
            <span class="text-gray-700 font-medium"></span>
          </div>
          <div class="flex justify-end">
            <base-button v-if="!showSearch" mode="outline" @click.stop="toggleSearch">Search</base-button>
            <base-button v-else @click.stop="toggleSearch">Cancel</base-button>
          </div>
        </div>

        <!-- Collapsible Search Section -->
        <div v-if="showSearch" class="flex flex-col mx-4 bg-gray-200 mt-4 pb-4 rounded-lg">
          <!-- Search and Dates -->
          <div class="w-full grid gap-6 mt-4 px-4 grid-cols-3">
            <div class="flex flex-col">
              <label class="text-gray-500 text-sm mb-1">Order ID</label>
              <input
                v-model.trim="searchKeyWord"
                type="text"
                placeholder="Search"
              />
            </div>
            <div class="flex flex-col">
              <label class="text-gray-500 text-sm mb-1">Start Date</label>
              <input type="date" id="startDate" v-model.trim="startDate" />
            </div>
            <div class="flex flex-col">
              <label class="text-gray-500 text-sm mb-1">End Date</label>
              <input type="date" id="endDate" v-model.trim="endDate" />
            </div>
          </div>

          <!-- Change Payment Status Section -->
          <div class="flex flex-col border p-4 rounded-lg mx-4 mt-4">
            <h1 class="text-lg font-medium">Change Payment Status</h1>
            <div class="flex justify-start items-center flex-wrap mt-2">
              <div class="flex justify-start items-center mr-8">
                <div class="flex-shrink-0 mr-2 text-sm">From</div>
                <select v-model="paymentStatus">
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                  <option value="rejected">Rejected</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
              <div class="flex justify-start items-center">
                <div class="flex-shrink-0 mr-2 text-sm">To</div>
                <select v-model="toPaymentStatus">
                  <option value="failed">Failed</option>
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="unassigned">Unassigned</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>
          </div>

          <!-- Additional Filters -->
          <div class="w-full grid gap-6 mt-4 px-4 grid-cols-4">
            <div class="flex flex-col">
              <label class="text-gray-500 text-sm mb-1">Payment Type</label>
              <select v-model="paymentType">
                <option value="payin">Pay In</option>
                <option value="payout">Pay Out</option>
              </select>
            </div>
            <div class="flex flex-col">
              <label class="text-gray-500 text-sm mb-1">Customer UPI</label>
              <select v-model="customerUPIID">
                <option value="">all</option>
                <option value="1">With Customer UPI</option>
                <option value="2">Without Customer UPI</option>
              </select>
            </div>
            <div class="flex flex-col">
              <label class="text-gray-500 text-sm mb-1">Records Per Page</label>
              <select v-model="first">
                <option value="10">10</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
          </div>

          <!-- Clear Button -->
          <div class="flex justify-start mt-4 px-4">
            <base-button @click.stop="clearBtnClicked">Clear All</base-button>
          </div>
        </div>

        <!-- Data Section -->
        <base-spinner v-if="isLoading" class="flex-auto"></base-spinner>
        <div
          v-else-if="hasFailedOrders && !isLoading"
          class="flex-auto flex flex-col mt-4 overflow-y-auto px-4 mb-4"
        >
          <div class="sm:-mx-6 lg:-mx-8">
            <div class="inline-block min-w-full sm:px-6 lg:px-8">
              <div class="overflow-x-auto">
                <table class="min-w-full text-left text-sm font-light">
                  <thead class="border-b font-medium dark:border-neutral-500">
                    <tr>
                      <th scope="col" class="px-6 py-4">
                        <input
                          type="checkbox"
                          v-model="selectAll"
                          @change="selectAllRows"
                        />
                      </th>
                      <th scope="col" class="px-6 py-4">#</th>
                      <th scope="col" class="px-6 py-4">Order ID</th>
                      <th scope="col" class="px-6 py-4">Receipt ID</th>
                      <th scope="col" class="px-6 py-4">Website</th>
                      <th scope="col" class="px-6 py-4">Customer Name</th>
                      <th scope="col" class="px-6 py-4">Amount</th>
                      <th scope="col" class="px-6 py-4">Payment Status</th>
                      <th scope="col" class="px-6 py-4">UTR</th>
                      <th scope="col" class="px-6 py-4">Assigned To</th>
                      <th scope="col" class="px-6 py-4">Assigned UPI</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(item, index) in failedOrders"
                      :key="index"
                      class="border-b dark:border-neutral-500"
                    >
                      <td class="whitespace-nowrap px-6 py-4 font-medium">
                        <input type="checkbox" v-model="item.isSelected" />
                      </td>
                      <td class="whitespace-nowrap px-6 py-4 font-medium">
                        {{ paginatorInfo.firstItem + index }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.merchantOrderId }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4 uppercase">
                        {{ item.receiptId }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.website }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.customerName }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.amount }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.paymentStatus }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.transactionID }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.validatorUsername }}
                      </td>
                      <td class="whitespace-nowrap px-6 py-4">
                        {{ item.validatorUPIID }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div class="flex justify-between items-center mt-4">
            <base-button mode="outline" @click.stop="submit"
              >Send Callback</base-button
            >
          </div>

          <div class="mt-4">
            <pagination
              :paginatorInfo="paginatorInfo"
              :setPage="setPage"
            ></pagination>
          </div>
        </div>
        <h3 v-else class="text-center font-medium flex-auto flex flex-col justify-center items-center text-base sm:text-xl text-gray-500">
          No <span class="capitalize">{{ paymentStatus }}</span> Orders found!
        </h3>
      </base-card>
    </section>
  </div>
</template>

<script>
import http from "../../http-common";
import Pagination from "../../components/shared/pagination.vue";

export default {
  name: "callback",
  components: {
    Pagination,
  },
  data() {
    return {
      isLoading: false,
      loadSeq: 0,
      searchDebounceTimer: null,
      error: null,
      first: 10,
      page: 1,
      failedOrders: [],
      paginatorInfo: [],
      selectAll: false,
      baseToast: {
        type: "",
        message: "",
      },
      showSearch: false,
      searchKeyWord: "",
      startDate: "",
      endDate: "",
      paymentStatus: "pending",
      toPaymentStatus: "failed",
      formIsValid: true,
      paymentType: "payin",
      customerUPIID: "2",
    };
  },
  watch: {
    page() {
      this.getFailedOrders();
    },
    first() {
      this.getFailedOrders();
    },
    // Typed inputs debounce: one request per pause, not one per keystroke.
    searchKeyWord() {
      this.debouncedReload();
    },
    startDate(newVal) {
      if (this.endDate || newVal == "") {
        this.page = 1;
        this.getFailedOrders();
      }
    },
    endDate(newVal) {
      if (this.startDate || newVal == "") {
        this.page = 1;
        this.getFailedOrders();
      }
    },
    paymentStatus() {
      this.page = 1;
      this.getFailedOrders();
    },
    paymentType() {
      this.page = 1;
      this.getFailedOrders();
    },
    customerUPIID() {
      this.debouncedReload();
    },
  },
  computed: {
    hasFailedOrders() {
      return this.failedOrders && this.failedOrders.length > 0;
    },
  },
  created() {
    this.getFailedOrders();
  },
  methods: {
    setPage(page) {
      this.page = page;
    },
    toggleSearch() {
      this.showSearch = !this.showSearch;
    },
    async getFailedOrders() {
      // Latest-wins: overlapping responses resolve in arbitrary order; only
      // the newest request may write the list/pagination/spinner.
      const seq = ++this.loadSeq;
      this.isLoading = true;
      try {
        let url = `/callback?first=${this.first}&page=${this.page}&searchKeyWord=${this.searchKeyWord}&startDate=${this.startDate}&endDate=${this.endDate}&paymentStatus=${this.paymentStatus}&paymentType=${this.paymentType}&customerUPIID=${this.customerUPIID}`;
        const response = await http.get(url);
        if (seq !== this.loadSeq) return;
        if (response.status == 200) {
          this.failedOrders = response.data.data.map((row) => {
            return {
              ...row,
              isSelected: false,
            };
          });
          this.paginatorInfo = response.data.pagination;
        } else {
          this.error = new Error("Unauthorized: You are not an admin.");
        }
      } catch (error) {
        if (seq === this.loadSeq) this.error = error.message || "Something failed!";
      } finally {
        if (seq === this.loadSeq) this.isLoading = false;
      }
    },
    debouncedReload() {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1;
        this.getFailedOrders();
      }, 300);
    },
    handleError() {
      this.error = null;
    },
    selectAllRows() {
      this.failedOrders.forEach((row) => {
        row.isSelected = this.selectAll;
      });
    },
    validateForm() {
      this.formIsValid = true;

      let message = "";

      if (this.paymentStatus == "") {
        message = "You must select From Payment Status.";
        this.formIsValid = false;
      }

      if (this.toPaymentStatus == "") {
        message += "You must select To Payment Status.";
        this.formIsValid = false;
      }

      return message;

    },
    async submit() {

      let error_msg = this.validateForm();
      if (!this.formIsValid) {

        this.$swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: error_msg,
        }).then((result) => {
            if (result.value || result.dismiss) {
                error_msg = null
            }
        })

        return;
      }

      const selectedIds = this.failedOrders
        .filter((row) => row.isSelected)
        .map((row) => row.refID);

      if (selectedIds.length == 0) {

        this.$swal.fire({
            icon: 'error',
            title: 'Oops...',
            text: "You must select at least 1 order!",
        }).then((result) => {
            if (result.value || result.dismiss) {
              console.log('You must select at least 1 order!');
            }
        })

        return;
      }

      this.isLoading = true;

      let url = `/callback`;
      try {
        const response = await http.post(url, { selectedIds: selectedIds, paymentStatus: this.toPaymentStatus });
        if (response.status == 201) {
          this.baseToast.type = "success";
          this.baseToast.message = response.data.message;
          setTimeout(() => {
            this.handleBaseToast();
            this.getFailedOrders();
          }, 1000);
        }
      } catch (error) {
        this.error = error.message || "Something failed!";
      }

      this.isLoading = false;
      this.selectAll = false;
      this.page = 1;
    },
    handleBaseToast() {
      this.baseToast = {
        type: "",
        message: "",
      };
    },
    clearBtnClicked() {
      this.searchKeyWord = "";
      this.startDate = "";
      this.endDate = "";
    },
  },
};
</script>
